import { zodResolver } from "@hookform/resolvers/zod";
import CloseIcon from "@mui/icons-material/Close";
import {
	Alert,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	Grid,
	IconButton,
	Radio,
	RadioGroup,
	Typography,
	useMediaQuery,
	useTheme
} from "@mui/material";
import { getTrailerCount } from "code/bookingMotorisedVehicleSlots";
import { useNotification } from "components/NotificationProvider";
import {
	buildUpsertVehicleSchema,
	UpsertVehicleSchema
} from "components/personnelAndFleet/fleet/UpsertVehicleSchema";
import ResponsiveButton from "components/ResponsiveButton";
import SelectInput, { SelectOption } from "components/SelectInput";
import TextInput from "components/TextInput";
import { Role } from "enums/Role";
import {
	useCreateVehicle,
	useGetFleetVehicleTypes,
	useUpdateVehicle,
	type UpsertVehicleRequest
} from "hooks/fleetVehicles";
import { useGetPersonnelUsers } from "hooks/personnelUsers";
import { useEffect, useMemo, useRef } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { z } from "zod";

type VehicleFormValues = z.input<typeof UpsertVehicleSchema>;

const defaultVehicle: VehicleFormValues = {
	registration: "",
	type: "",
	lengthM: "",
	refrigerated: "",
	driverContactId: null,
	tractorRego: "",
	trailer1Rego: "",
	trailer2Rego: "",
	status: ""
};

interface UpsertVehicleModalProps {
	open: boolean;
	onClose: () => void;
	vehicle?:
		| (Partial<VehicleFormValues> & { id?: number; defaultDriver?: string; driverActive?: boolean })
		| null;
}

export default function UpsertVehicleModal({
	open,
	onClose,
	vehicle = null
}: UpsertVehicleModalProps) {
	const theme = useTheme();
	const isFullScreen = useMediaQuery(theme.breakpoints.down("md"));
	const isEditMode = vehicle !== null && vehicle.id && vehicle.id > 0;
	const { createVehicle, isCreating } = useCreateVehicle();
	const { updateVehicle, isUpdating } = useUpdateVehicle();
	const {
		data: vehicleTypes,
		isDataLoading: isVehicleTypesLoading,
		dataError: vehicleTypesError
	} = useGetFleetVehicleTypes(open);
	const { data: personnel } = useGetPersonnelUsers();
	const { showNotification } = useNotification();
	const isSaving = isCreating || isUpdating;

	const maxLengthRef = useRef<number | null>(null);
	const schemaRef = useRef(buildUpsertVehicleSchema(null));
	const resolverRef = useRef(zodResolver(buildUpsertVehicleSchema(null)));
	const hasResetForCurrentOpenRef = useRef(false);

	const updateMaxLength = (newMax: number | null) => {
		maxLengthRef.current = newMax;
		schemaRef.current = buildUpsertVehicleSchema(newMax);
		resolverRef.current = zodResolver(schemaRef.current);
	};

	const personnelOptions = useMemo(() => {
		const activeOptions = personnel
			.filter((p) => p.active === true && p.roles.includes(Role.DRIVER))
			.map((p) => ({
				label: `${p.firstName} ${p.lastName}`,
				value: p.id
			}))
			.sort((a, b) => a.label.localeCompare(b.label)) as SelectOption[];

		// If the assigned driver is deactivated they won't be in the active list —
		// add them as a synthetic option so the dropdown shows their name instead of the raw ID.
		const assignedId = vehicle?.driverContactId;
		const assignedName = vehicle?.defaultDriver;
		if (assignedId != null && assignedName && !activeOptions.some((o) => o.value === assignedId)) {
			activeOptions.unshift({ label: assignedName, value: assignedId, disabled: true });
		}

		return activeOptions;
	}, [personnel, vehicle?.driverContactId, vehicle?.defaultDriver]);

	const vehicleTypeOptions = useMemo(
		() =>
			vehicleTypes.map((type) => ({
				label: type.vehicleName,
				value: String(type.vehicleTypeID)
			})) as SelectOption[],
		[vehicleTypes]
	);

	const initialFormValues = useMemo(() => {
		const sourceType = String(vehicle?.type ?? "");
		const matchedByName = vehicleTypes.find((item) => item.vehicleName === sourceType);
		const resolvedType = matchedByName ? String(matchedByName.vehicleTypeID) : sourceType;
		const resolvedRegistration = String(vehicle?.registration ?? "").toUpperCase();

		return {
			...defaultVehicle,
			...(vehicle ?? {}),
			registration: resolvedRegistration,
			type: resolvedType
		};
	}, [vehicle, vehicleTypes]);

	const {
		control,
		handleSubmit,
		reset,
		trigger,
		setValue,
		getValues,
		formState: { isSubmitted, isValid, isDirty }
	} = useForm<VehicleFormValues>({
		defaultValues: initialFormValues,
		resolver: (values, context, options) => resolverRef.current(values, context, options),
		mode: "onChange"
	});

	const currentDriverId = useWatch({ control, name: "driverContactId" });
	const currentTypeId = useWatch({ control, name: "type" });

	const selectedVehicleType = useMemo(
		() => vehicleTypes.find((t) => String(t.vehicleTypeID) === String(currentTypeId ?? "")) ?? null,
		[vehicleTypes, currentTypeId]
	);
	const trailerCount = getTrailerCount(selectedVehicleType?.registrationPlates);
	const showTrailer1 = trailerCount >= 1;
	const showTrailer2 = trailerCount >= 2;

	const regoFields = ["tractorRego", "trailer1Rego", "trailer2Rego"] as const;
	type RegoField = (typeof regoFields)[number];

	const handleRegoChange = (field: RegoField, value: string, onChange: (v: string) => void) => {
		onChange(value.toUpperCase());
		const others = regoFields.filter((f) => f !== field);
		trigger(others);
	};

	useEffect(() => {
		if (!open) {
			hasResetForCurrentOpenRef.current = false;
			return;
		}
		if (hasResetForCurrentOpenRef.current) {
			return;
		}
		// In edit mode, wait for vehicle types to load so the type ID resolves correctly
		if (
			vehicle?.type &&
			vehicleTypes.length === 0 &&
			(isVehicleTypesLoading || !!vehicleTypesError)
		) {
			return;
		}

		hasResetForCurrentOpenRef.current = true;
		const initialTypeId = String(initialFormValues.type);
		const matched = vehicleTypes.find((t) => String(t.vehicleTypeID) === initialTypeId) ?? null;
		updateMaxLength(matched?.maxLength ?? null);
		reset(initialFormValues);
	}, [
		open,
		reset,
		initialFormValues,
		vehicleTypes,
		isVehicleTypesLoading,
		vehicleTypesError,
		vehicle?.type
	]);

	const handleCancel = () => {
		reset(initialFormValues);
		onClose();
	};

	const handleTypeChange = (value: string | number | null, onChange: (value: string) => void) => {
		const newTypeId = String(value ?? "");
		onChange(newTypeId);
		const matched = vehicleTypes.find((t) => String(t.vehicleTypeID) === newTypeId) ?? null;
		updateMaxLength(matched?.maxLength ?? null);
		if (matched?.defaultLength != null && matched.defaultLength > 0) {
			setValue("lengthM", String(matched.defaultLength));
			trigger("lengthM");
		} else if (getValues("lengthM")) {
			// Field already has a value — re-validate against the new type's max length
			trigger("lengthM");
		}

		// Clear rego fields that the new type no longer shows so a stale value from a
		// previously selected type isn't silently submitted.
		const newTrailerCount = getTrailerCount(matched?.registrationPlates);
		if (newTrailerCount < 1 && getValues("trailer1Rego")) {
			setValue("trailer1Rego", "");
		}
		if (newTrailerCount < 2 && getValues("trailer2Rego")) {
			setValue("trailer2Rego", "");
		}
	};

	const onSubmit = async (values: VehicleFormValues) => {
		const payload: UpsertVehicleRequest = {
			registration: String(values.registration ?? "").toUpperCase(),
			fleetVehicleTypeId: Number(values.type),
			lengthM: values.lengthM === "" ? 0 : Number(values.lengthM),
			refrigerated: values.refrigerated,
			driverContactId: values.driverContactId ?? null,
			tractorRego: values.tractorRego,
			trailer1Rego: values.trailer1Rego,
			trailer2Rego: values.trailer2Rego,
			status: values.status
		};

		if (isEditMode) {
			if (!vehicle?.id) {
				console.error("Cannot update vehicle without id");
				showNotification({ message: "Unable to save vehicle data", severity: "error" });
				return;
			}

			const result = await updateVehicle(vehicle.id, payload);
			if (!result.success) {
				showNotification({ message: result.errorMessage, severity: "error" });
				return;
			}
			showNotification({ message: "Fleet vehicle updated successfully", severity: "success" });
		} else {
			const result = await createVehicle(payload);
			if (!result.success) {
				showNotification({ message: result.errorMessage, severity: "error" });
				return;
			}
			showNotification({ message: "Fleet vehicle added successfully", severity: "success" });
		}

		onClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleCancel}
			maxWidth="sm"
			fullWidth
			fullScreen={isFullScreen}
			slotProps={{ transition: { onExited: () => reset(defaultVehicle) } }}
		>
			<DialogTitle
				variant="text20Bold"
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					pb: 2,
					px: { xs: 2, md: 4 },
					borderBottom: 1,
					borderColor: "divider"
				}}
			>
				{isEditMode ? "Edit fleet" : "Add new vehicle to fleet"}
				<IconButton onClick={handleCancel} size="small" aria-label="Close">
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent sx={{ p: 0 }}>
				{vehicleTypesError && (
					<Alert severity="error" sx={{ mx: { xs: 2, md: 4 }, mt: { xs: 2, md: 4 } }}>
						Failed to load vehicle types. Please close and try again.
					</Alert>
				)}
				<Grid container spacing={{ xs: 1.5, md: 3 }} sx={{ p: { xs: 2, md: 4 } }}>
					<Grid size={12}>
						<Typography variant="text20Bold" gutterBottom>
							Vehicle details
						</Typography>
					</Grid>
					<Grid size={{ xs: 12, sm: 6 }}>
						<Controller
							name="registration"
							control={control}
							render={({ field, fieldState }) => (
								<TextInput
									label="Fleet Number"
									value={field.value}
									onChange={field.onChange}
									error={!!fieldState.error}
									helperText={fieldState.error?.message}
									required
								/>
							)}
						/>
					</Grid>
					<Grid size={{ xs: 12, sm: 6 }}>
						<Controller
							name="type"
							control={control}
							render={({ field, fieldState }) => (
								<SelectInput
									label="Type"
									options={vehicleTypeOptions}
									value={String(field.value ?? "")}
									onChange={(value) => handleTypeChange(value, field.onChange)}
									loading={isVehicleTypesLoading}
									error={!!fieldState.error}
									helperText={fieldState.error?.message}
									required
								/>
							)}
						/>
					</Grid>

					<Grid size={{ xs: 12, sm: 6 }}>
						<Controller
							name="lengthM"
							control={control}
							render={({ field, fieldState }) => (
								<TextInput
									label="Length (M)"
									type="number"
									value={field.value}
									onChange={field.onChange}
									error={!!fieldState.error}
									helperText={fieldState.error?.message}
									slotProps={{ htmlInput: { step: 0.1 } }}
									required
								/>
							)}
						/>
					</Grid>
					<Grid
						size={{ xs: 12, sm: 6 }}
						sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}
					>
						<Controller
							name="refrigerated"
							control={control}
							render={({ field, fieldState }) => (
								<>
									<Typography
										variant="text12Uppercase"
										sx={{ mb: 0.5, color: fieldState.error ? "error.main" : "secondary.light50" }}
									>
										Refrigerated *
									</Typography>
									<RadioGroup row value={field.value} onChange={field.onChange}>
										<FormControlLabel
											value="yes"
											control={<Radio size="small" />}
											label={<Typography variant="text14">Yes</Typography>}
										/>
										<FormControlLabel
											value="no"
											control={<Radio size="small" />}
											label={<Typography variant="text14">No</Typography>}
										/>
									</RadioGroup>
									{fieldState.error && (
										<Typography variant="text12" color="error">
											{fieldState.error.message}
										</Typography>
									)}
								</>
							)}
						/>
					</Grid>

					<Grid size={12}>
						<Divider />
					</Grid>

					<Grid size={12}>
						<Typography variant="text20Bold" gutterBottom>
							Additional details
						</Typography>
					</Grid>

					<Grid size={{ xs: 12, sm: 6 }}>
						<Controller
							name="driverContactId"
							control={control}
							render={({ field, fieldState }) => (
								<SelectInput
									label="Default Driver"
									options={personnelOptions}
									value={field.value ?? null}
									onChange={(val) => field.onChange(val !== null ? Number(val) : null)}
									placeholder="Select a driver"
									clearable
									error={!!fieldState.error}
									helperText={fieldState.error?.message}
								/>
							)}
						/>
						{vehicle?.driverActive === false && currentDriverId === vehicle?.driverContactId && (
							<Alert severity="warning" sx={{ mt: 1 }}>
								The assigned default driver is deactivated.
							</Alert>
						)}
					</Grid>
					<Grid size={{ xs: 12, sm: 6 }}>
						<Controller
							name="tractorRego"
							control={control}
							render={({ field, fieldState }) => (
								<TextInput
									label="Registration Truck/Tractor"
									value={field.value ?? ""}
									onChange={(value) => handleRegoChange("tractorRego", value, field.onChange)}
									error={!!fieldState.error}
									helperText={fieldState.error?.message}
								/>
							)}
						/>
					</Grid>
					{showTrailer1 && (
						<Grid size={{ xs: 12, sm: 6 }}>
							<Controller
								name="trailer1Rego"
								control={control}
								render={({ field, fieldState }) => (
									<TextInput
										label="Registration Trailer 1"
										value={field.value ?? ""}
										onChange={(value) => handleRegoChange("trailer1Rego", value, field.onChange)}
										error={!!fieldState.error}
										helperText={fieldState.error?.message}
									/>
								)}
							/>
						</Grid>
					)}
					{showTrailer2 && (
						<Grid size={{ xs: 12, sm: 6 }}>
							<Controller
								name="trailer2Rego"
								control={control}
								render={({ field, fieldState }) => (
									<TextInput
										label="Registration Trailer 2"
										value={field.value ?? ""}
										onChange={(value) => handleRegoChange("trailer2Rego", value, field.onChange)}
										error={!!fieldState.error}
										helperText={fieldState.error?.message}
									/>
								)}
							/>
						</Grid>
					)}

					<Grid size={12}>
						<Divider />
					</Grid>

					<Grid size={{ xs: 12, sm: 6 }}>
						<Controller
							name="status"
							control={control}
							render={({ field, fieldState }) => (
								<SelectInput
									label="Status"
									options={
										[
											{ label: "Active", value: "Active" },
											{ label: "Deactivated", value: "Deactivated" }
										] as SelectOption[]
									}
									value={field.value}
									onChange={field.onChange}
									error={!!fieldState.error}
									helperText={fieldState.error?.message}
									required
								/>
							)}
						/>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions
				disableSpacing
				sx={{
					borderTop: 1,
					borderColor: "divider",
					flexDirection: { xs: "column-reverse", sm: "row" },
					justifyContent: "flex-end",
					gap: 2,
					p: 2
				}}
			>
				<ResponsiveButton variant="text" onClick={handleCancel}>
					Cancel
				</ResponsiveButton>
				<ResponsiveButton
					variant="contained"
					onClick={handleSubmit(onSubmit)}
					disabled={
						isSaving ||
						(isEditMode ? !isDirty || (isSubmitted && !isValid) : isSubmitted && !isValid)
					}
				>
					{isEditMode ? "Save updates" : "Add vehicle"}
				</ResponsiveButton>
			</DialogActions>
		</Dialog>
	);
}
